import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit3, Trash2, IndianRupee, FileText, CheckCircle, AlertTriangle, 
  Settings, HelpCircle, Save, X, Info, Search, Users, Calendar
} from 'lucide-react';
import { 
  fetchTaxConfigurations, saveTaxConfiguration,
  fetchEmployeeRegimes, bulkAssignRegime, updateEmployeeTaxProfile
} from '../../services/incomeTaxApi';

const inputClass = 'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

const emptyTaxConfig = () => ({
  name: '',
  effective_from: new Date().toISOString().slice(0, 10),
  effective_to: '',
  status: 'draft',
  regime: 'NEW',
  income_tax_slabs: [{ min: 0, max: null, rate: 0 }]
});


const IncomeTaxConfiguration = () => {
  const { hasPermission } = useAuth();
  const [activeTab, setActiveTab] = useState('configs');
  const [taxConfigs, setTaxConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const { showSuccess, showError } = useNotification();
  const [saving, setSaving] = useState(false);
  const setError = (msg) => { if (msg) showError(msg); };
  const setSuccess = (msg) => { if (msg) showSuccess(msg); };
  
  // Modals state
  const [activeConfigModal, setActiveConfigModal] = useState(null); // { data } | null
  
  // Employee Regime Assignment state
  const [taxYear, setTaxYear] = useState('2026-27');
  const [employees, setEmployees] = useState([]);
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRegime, setFilterRegime] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkRegime, setBulkRegime] = useState('');
  const [bulkEffectiveFrom, setBulkEffectiveFrom] = useState(new Date().toISOString().slice(0, 10));
  const [skip, setSkip] = useState(0);
  const [limit] = useState(50);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (activeTab === 'regimes') {
      loadEmployees();
    }
  }, [activeTab, taxYear, searchTerm, filterRegime, filterStatus, skip]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchTaxConfigurations();
      setTaxConfigs(data);
    } catch (err) {
      setError(err.message || 'Failed to load details');
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    setLoadingEmployees(true);
    setError('');
    try {
      const filters = {
        search: searchTerm,
        regime: filterRegime,
        status: filterStatus,
        skip,
        limit
      };
      const data = await fetchEmployeeRegimes(taxYear, filters);
      setEmployees(data.employees || []);
      setTotalEmployees(data.total || 0);
    } catch (err) {
      setError(err.message || 'Failed to load employees');
    } finally {
      setLoadingEmployees(false);
    }
  };

  const handleIndividualRegimeChange = async (employeeId, newRegime) => {
    try {
      await updateEmployeeTaxProfile(employeeId, taxYear, newRegime);
      showMessage(`Regime updated for employee`);
      loadEmployees();
    } catch (err) {
      showMessage(err.message || 'Failed to update regime', true);
    }
  };

  const handleBulkAssign = async () => {
    if (selectedEmployees.length === 0) {
      showMessage('Please select at least one employee', true);
      return;
    }
    if (!bulkRegime) {
      showMessage('Please select a regime', true);
      return;
    }

    setSaving(true);
    try {
      const result = await bulkAssignRegime(selectedEmployees, bulkRegime, taxYear, bulkEffectiveFrom);
      showMessage(`Bulk assignment completed: ${result.updated_count} updated, ${result.created_count} created`);
      setShowBulkModal(false);
      setSelectedEmployees([]);
      setBulkRegime('');
      loadEmployees();
    } catch (err) {
      showMessage(err.message || 'Bulk assignment failed', true);
    } finally {
      setSaving(false);
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedEmployees(employees.map(e => e.employee_id));
    } else {
      setSelectedEmployees([]);
    }
  };

  const showMessage = (msg, isError = false) => {
    if (isError) {
      setError(msg);
      setTimeout(() => setError(''), 5000);
    } else {
      setSuccess(msg);
      setTimeout(() => setSuccess(''), 5000);
    }
  };

  // Slab handlers
  const addSlab = () => {
    if (!activeConfigModal) return;
    const slabs = [...(activeConfigModal.data.income_tax_slabs || [])];
    const lastSlab = slabs[slabs.length - 1];
    const nextMin = lastSlab ? (lastSlab.max || lastSlab.min + 300000) : 0;
    slabs.push({ min: nextMin, max: null, rate: 0 });
    setActiveConfigModal({ ...activeConfigModal, data: { ...activeConfigModal.data, income_tax_slabs: slabs } });
  };

  const removeSlab = (index) => {
    if (!activeConfigModal) return;
    const slabs = (activeConfigModal.data.income_tax_slabs || []).filter((_, idx) => idx !== index);
    setActiveConfigModal({ ...activeConfigModal, data: { ...activeConfigModal.data, income_tax_slabs: slabs } });
  };

  const updateSlab = (index, key, val) => {
    if (!activeConfigModal) return;
    const slabs = [...(activeConfigModal.data.income_tax_slabs || [])];
    slabs[index] = { ...slabs[index], [key]: val };
    setActiveConfigModal({ ...activeConfigModal, data: { ...activeConfigModal.data, income_tax_slabs: slabs } });
  };

  // Validators
  const validateSlabs = (slabs) => {
    if (!slabs || slabs.length === 0) return 'At least one tax slab must exist.';
    
    // Check basic slab values
    for (let i = 0; i < slabs.length; i++) {
      const s = slabs[i];
      if (s.min === null || s.min === undefined || s.min === '' || Number(s.min) < 0) {
        return `Slab ${i + 1}: Minimum income must be greater than or equal to 0.`;
      }
      if (s.rate === null || s.rate === undefined || s.rate === '' || Number(s.rate) < 0 || Number(s.rate) > 100) {
        return `Slab ${i + 1}: Tax rate must be between 0% and 100%.`;
      }
      if (s.max !== null && s.max !== undefined && s.max !== '') {
        if (Number(s.max) <= Number(s.min)) {
          return `Slab ${i + 1}: Maximum income (${s.max}) must be greater than minimum income (${s.min}).`;
        }
      }
    }

    // Sort slabs by min_income to validate
    const sorted = [...slabs].sort((a, b) => Number(a.min) - Number(b.min));

    // Check if original slabs are already sorted by min_income
    for (let i = 0; i < slabs.length; i++) {
      if (Number(slabs[i].min) !== Number(sorted[i].min)) {
        return 'Slabs must be sorted in ascending order of minimum income.';
      }
    }

    // First slab must start at 0
    if (Number(sorted[0].min) !== 0) {
      return 'The first tax slab minimum income must be 0.';
    }

    // Duplicate slabs check (same min and max)
    for (let i = 0; i < sorted.length - 1; i++) {
      const curr = sorted[i];
      const next = sorted[i + 1];
      if (Number(curr.min) === Number(next.min) && curr.max === next.max) {
        return `Duplicate slab range detected: ${curr.min} to ${curr.max || 'No Limit'}.`;
      }
    }

    // Check for overlaps, gaps, and ensure only last slab can be open-ended
    for (let i = 0; i < sorted.length; i++) {
      const curr = sorted[i];
      
      if (i < sorted.length - 1) {
        const next = sorted[i + 1];
        
        if (curr.max === null || curr.max === undefined || curr.max === '') {
          return 'Only the last slab can be open-ended ("No Limit"). There cannot be slabs after a "No Limit" slab.';
        }
        
        if (Number(next.min) !== Number(curr.max)) {
          return `Slabs must be contiguous: next slab starts at ${next.min} but current ends at ${curr.max}.`;
        }
      } else {
        // Last slab checks
        if (curr.max !== null && curr.max !== undefined && curr.max !== '') {
          return 'The last slab must be open-ended (maximum income set to "No Limit").';
        }
      }
    }

    return null;
  };

  const handleSaveConfig = async (e) => {
    e.preventDefault();
    const configData = activeConfigModal.data;
    
    // Validate slabs
    const slabError = validateSlabs(configData.income_tax_slabs);
    if (slabError) {
      showMessage(slabError, true);
      return;
    }

    if (configData.effective_to) {
      if (new Date(configData.effective_to) < new Date(configData.effective_from)) {
        showMessage('Effective To date must be greater than or equal to Effective From date.', true);
        return;
      }
    }

    setSaving(true);
    try {
      // Build simplified payload containing only the 6 admin-configurable fields
      const payload = {
        name: configData.name,
        status: configData.status,
        regime: configData.regime,
        effective_from: configData.effective_from,
        effective_to: configData.effective_to || null,
        income_tax_slabs: configData.income_tax_slabs.map(s => ({
          min: Number(s.min),
          max: s.max === null || s.max === undefined || s.max === '' ? null : Number(s.max),
          rate: Number(s.rate)
        }))
      };

      if (configData.id) {
        payload.id = configData.id;
      }

      await saveTaxConfiguration(payload);
      showMessage('Tax configuration saved successfully');
      setActiveConfigModal(null);
      loadData();
    } catch (err) {
      showMessage(err.message || 'Save failed', true);
    } finally {
      setSaving(false);
    }
  };


  const formatCurrency = (val) => 
    val !== null && val !== undefined ? `₹${parseFloat(val).toLocaleString('en-IN')}` : '—';

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header Banner */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h1 className="text-2xl font-bold text-gray-900">Income Tax Configuration</h1>
        <p className="text-sm text-gray-500 mt-1">
          Configure financial-year-wise Income Tax regimes and progressive slab-based calculation rules.
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          type="button"
          onClick={() => setActiveTab('configs')}
          className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
            activeTab === 'configs' 
              ? 'border-blue-600 text-blue-700' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Tax Year Configurations
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('regimes')}
          className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
            activeTab === 'regimes' 
              ? 'border-blue-600 text-blue-700' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Employee Tax Regime Assignment
        </button>
      </div>


      {/* Main Grid Content */}
      {activeTab === 'configs' ? (
        loading ? (
          <div className="py-12 text-center text-gray-500 font-medium bg-white rounded-xl border border-gray-100 shadow-sm">Loading config data...</div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-bold text-gray-900">Tax Year Configurations</h2>
                {hasPermission('itconfig:add') && (
<button
                  type="button"
                  onClick={() => setActiveConfigModal({ data: emptyTaxConfig() })}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-colors"
                >
                  <Plus className="w-4 h-4"/> Add Configuration
                </button>
)}
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-100 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Configuration Name</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Regime</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Effective From</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Effective To</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">No. of Slabs</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {taxConfigs.map((cfg) => (
                      <tr key={cfg.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3.5 font-semibold text-gray-900">{cfg.name}</td>
                        <td className="px-4 py-3.5">
                          <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                            cfg.regime === 'NEW' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                          }`}>
                            {cfg.regime === 'NEW' ? 'New Regime' : 'Old Regime'}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 text-gray-700">{cfg.effective_from}</td>
                        <td className="px-4 py-3.5 text-gray-700">{cfg.effective_to || 'No Limit'}</td>
                        <td className="px-4 py-3.5 font-mono font-medium text-gray-700">{(cfg.income_tax_slabs || []).length}</td>
                        <td className="px-4 py-3.5">
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase ${
                            cfg.status === 'active' ? 'bg-green-100 text-green-800' :
                            cfg.status === 'draft' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {cfg.status === 'active' ? 'ACTIVE' : cfg.status === 'draft' ? 'DRAFT' : 'INACTIVE'}
                          </span>
                        </td>
                        <td className="px-4 py-3.5">
                          {hasPermission('itconfig:edit') && (
<button
                            type="button"
                            onClick={() => setActiveConfigModal({ data: { ...cfg } })}
                            className="text-blue-600 font-semibold hover:underline flex items-center gap-1 text-xs"
                          >
                            <Edit3 className="w-3.5 h-3.5" /> Edit
                          </button>
)}
                        </td>
                      </tr>
                    ))}
                    {taxConfigs.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-gray-500 italic">No configurations created. Click "Add Configuration" to explicitly set up tax rules.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )
      ) : (
        /* Employee Tax Regime Assignment Tab */
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="space-y-4">
            {/* Header with Tax Year selector */}
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Employee Tax Regime Assignment</h2>
                <p className="text-sm text-gray-500 mt-1">Assign Income Tax Regime (Old/New) to employees for the financial year.</p>
              </div>
              <div className="flex items-center gap-3">
                <label className="text-sm font-semibold text-gray-700">Financial Year:</label>
                <select
                  className={`${inputClass} w-32`}
                  value={taxYear}
                  onChange={(e) => setTaxYear(e.target.value)}
                >
                  <option value="2025-26">2025-26</option>
                  <option value="2026-27">2026-27</option>
                  <option value="2027-28">2027-28</option>
                </select>
              </div>
            </div>

            {/* Search and Filters */}
            <div className="flex flex-wrap gap-3 items-center">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by employee code or name..."
                    className={`${inputClass} pl-10`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <select
                className={`${inputClass} w-40`}
                value={filterRegime}
                onChange={(e) => setFilterRegime(e.target.value)}
              >
                <option value="">All Regimes</option>
                <option value="OLD">Old Regime</option>
                <option value="NEW">New Regime</option>
              </select>
              <select
                className={`${inputClass} w-40`}
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              {selectedEmployees.length > 0 && hasPermission('itconfig:assign') && (
<button
                  type="button"
                  onClick={() => setShowBulkModal(true)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-colors"
                >
                  <Users className="w-4 h-4"/> Assign Regime ({selectedEmployees.length})
                </button>
              )}
            </div>

            {/* Employee Table */}
            {loadingEmployees ? (
              <div className="py-12 text-center text-gray-500 font-medium">Loading employees...</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-100 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left w-10">
                        <input
                          type="checkbox"
                          checked={selectedEmployees.length === employees.length && employees.length > 0}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Employee Code</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Employee Name</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Current Regime</th>
                      {/* <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Effective From</th> */}
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {employees.map((emp) => (
                      <tr key={emp.employee_id} className="hover:bg-gray-50">
                        <td className="px-4 py-3.5">
                          <input
                            type="checkbox"
                            checked={selectedEmployees.includes(emp.employee_id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedEmployees([...selectedEmployees, emp.employee_id]);
                              } else {
                                setSelectedEmployees(selectedEmployees.filter(id => id !== emp.employee_id));
                              }
                            }}
                          />
                        </td>
                        <td className="px-4 py-3.5 font-mono font-semibold text-gray-900">{emp.employee_code}</td>
                        <td className="px-4 py-3.5 font-medium text-gray-900">{emp.employee_name}</td>
                        <td className="px-4 py-3.5">
                          {emp.has_profile ? (
                            <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                              emp.tax_regime === 'NEW' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                            }`}>
                              {emp.tax_regime === 'NEW' ? 'New Regime' : 'Old Regime'}
                            </span>
                          ) : (
                            <span className="text-gray-400 italic">Not Assigned</span>
                          )}
                        </td>
                        {/* <td className="px-4 py-3.5 text-gray-700">{emp.effective_from || '—'}</td> */}
                        <td className="px-4 py-3.5">
                          <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                            emp.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {emp.status}
                          </span>
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="flex gap-1">
                            {hasPermission('itconfig:assign') && (
<button
                              type="button"
                              onClick={() => handleIndividualRegimeChange(emp.employee_id, 'OLD')}
                              className={`px-2 py-1 text-xs font-semibold rounded transition-colors ${
                                emp.tax_regime === 'OLD' 
                                  ? 'bg-purple-600 text-white' 
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              OLD
                            </button>
)}
                            {hasPermission('itconfig:assign') && (
<button
                              type="button"
                              onClick={() => handleIndividualRegimeChange(emp.employee_id, 'NEW')}
                              className={`px-2 py-1 text-xs font-semibold rounded transition-colors ${
                                emp.tax_regime === 'NEW' 
                                  ? 'bg-blue-600 text-white' 
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              NEW
                            </button>
)}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {employees.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-gray-500 italic">No employees found matching the criteria.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalEmployees > limit && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Showing {skip + 1}-{Math.min(skip + limit, totalEmployees)} of {totalEmployees} employees</span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setSkip(Math.max(0, skip - limit))}
                    disabled={skip === 0}
                    className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={() => setSkip(skip + limit)}
                    disabled={skip + limit >= totalEmployees}
                    className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 1. Tax Config Modal */}
      {activeConfigModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex justify-center items-center p-4">
          <form onSubmit={handleSaveConfig} className="bg-white rounded-xl shadow-xl border w-full max-w-3xl overflow-hidden animate-in fade-in zoom-in">
            <div className="flex justify-between items-center px-6 py-4 bg-gray-50 border-b">
              <h3 className="font-bold text-gray-900">
                {activeConfigModal.data.id ? 'Edit Tax Configuration' : 'Add Tax Year Configuration'}
              </h3>
              <button type="button" onClick={() => setActiveConfigModal(null)} className="text-gray-400 hover:text-gray-600 font-semibold">✕</button>
            </div>
            
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto pr-2">
              <label>
                <span className="text-sm font-semibold">Configuration Name *</span>
                <input className={`${inputClass} mt-1`} value={activeConfigModal.data.name} onChange={(e) => setActiveConfigModal({ ...activeConfigModal, data: { ...activeConfigModal.data, name: e.target.value } })} required />
              </label>
              
              <label>
                <span className="text-sm font-semibold">Status *</span>
                <select className={`${inputClass} mt-1`} value={activeConfigModal.data.status ?? ''} onChange={(e) => setActiveConfigModal({ ...activeConfigModal, data: { ...activeConfigModal.data, status: e.target.value } })}>
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="disabled">Inactive</option>
                </select>
              </label>

              <label>
                <span className="text-sm font-semibold">Regime *</span>
                <select className={`${inputClass} mt-1`} value={activeConfigModal.data.regime ?? ''} onChange={(e) => setActiveConfigModal({ ...activeConfigModal, data: { ...activeConfigModal.data, regime: e.target.value } })}>
                  <option value="NEW">New Regime</option>
                  <option value="OLD">Old Regime</option>
                </select>
              </label>

              <label>
                <span className="text-sm font-semibold">Effective From *</span>
                <input type="date" className={`${inputClass} mt-1`} value={activeConfigModal.data.effective_from} onChange={(e) => setActiveConfigModal({ ...activeConfigModal, data: { ...activeConfigModal.data, effective_from: e.target.value } })} required />
              </label>

              <label>
                <span className="text-sm font-semibold">Effective To</span>
                <input type="date" className={`${inputClass} mt-1`} value={activeConfigModal.data.effective_to || ''} onChange={(e) => setActiveConfigModal({ ...activeConfigModal, data: { ...activeConfigModal.data, effective_to: e.target.value || null } })} />
              </label>

              {/* Slabs Section */}
              <div className="md:col-span-2 border-t pt-4 mt-2">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-bold text-sm text-gray-900">Income Tax Slabs</span>
                  <button type="button" onClick={addSlab} className="text-xs text-blue-600 font-semibold hover:underline">+ Add Slab</button>
                </div>
                <div className="space-y-2">
                  {(activeConfigModal.data.income_tax_slabs || []).map((slab, index) => (
                    <div key={index} className="flex items-center gap-2 bg-gray-50 p-2 rounded border border-gray-200">
                      <div className="flex-1">
                        <span className="text-xs text-gray-500">Min Income (₹)</span>
                        <input type="number" className={`${inputClass} mt-0.5`} value={slab.min} onChange={(e) => updateSlab(index, 'min', e.target.value === '' ? '' : Number(e.target.value))} />
                      </div>
                      <div className="flex-1">
                        <span className="text-xs text-gray-500">Max Income (₹)</span>
                        <input type="number" className={`${inputClass} mt-0.5`} placeholder="No limit" value={slab.max || ''} onChange={(e) => updateSlab(index, 'max', e.target.value ? Number(e.target.value) : null)} />
                      </div>
                      <div className="w-24">
                        <span className="text-xs text-gray-500">Rate (%)</span>
                        <input type="number" className={`${inputClass} mt-0.5`} value={slab.rate} onChange={(e) => updateSlab(index, 'rate', e.target.value === '' ? '' : Number(e.target.value))} />
                      </div>
                      <button type="button" onClick={() => removeSlab(index)} className="px-2 py-1 mt-4 text-xs font-semibold text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 rounded border border-red-200">Delete</button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 border-t">
              <button type="button" onClick={() => setActiveConfigModal(null)} className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm font-semibold">Cancel</button>
              <button type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold disabled:opacity-50 flex items-center gap-1">
                <Save className="w-4 h-4"/> Save Configuration
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Bulk Regime Assignment Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex justify-center items-center p-4">
          <div className="bg-white rounded-xl shadow-xl border w-full max-w-md overflow-hidden animate-in fade-in zoom-in">
            <div className="flex justify-between items-center px-6 py-4 bg-gray-50 border-b">
              <h3 className="font-bold text-gray-900">Bulk Assign Tax Regime</h3>
              <button type="button" onClick={() => setShowBulkModal(false)} className="text-gray-400 hover:text-gray-600 font-semibold">✕</button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="p-3 bg-blue-50 border border-blue-200 text-blue-800 rounded-lg text-sm">
                Assigning regime to <strong>{selectedEmployees.length}</strong> employee(s) for FY <strong>{taxYear}</strong>
              </div>

              <label>
                <span className="text-sm font-semibold">Tax Regime *</span>
                <select className={`${inputClass} mt-1`} value={bulkRegime} onChange={(e) => setBulkRegime(e.target.value)} required>
                  <option value="">Select regime</option>
                  <option value="OLD">Old Regime</option>
                  <option value="NEW">New Regime</option>
                </select>
              </label>

              <label>
                <span className="text-sm font-semibold">Effective From</span>
                <input type="date" className={`${inputClass} mt-1`} value={bulkEffectiveFrom} onChange={(e) => setBulkEffectiveFrom(e.target.value)} />
              </label>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 border-t">
              <button type="button" onClick={() => setShowBulkModal(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm font-semibold">Cancel</button>
              <button type="button" onClick={handleBulkAssign} disabled={saving} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold disabled:opacity-50">
                {saving ? 'Assigning...' : 'Assign Regime'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default IncomeTaxConfiguration;
